import { TABLE_TOP_CORNERS } from "../../v2/components/tableHeaderCorners.js";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { getUtilizationReport } from "../../data/reportsClient.js";
import { useSensitivityPolicy } from "../../contexts/SensitivityPolicyContext.jsx";
import { T } from "../../app/tokens.js";
import { OverflowTable } from "../../v2/components/ListOverflow.jsx";
import { ReportShell } from "./ReportShell.jsx";
import { ReportAccessNotice } from "./ReportShell.jsx";
import { useFeatureFlagContext } from "../../contexts/FeatureFlagContext.jsx";
import { getReportNavEligibility } from "./reportNavEligibility.js";
import { FilterDropdown } from "../../components/FilterDropdown.jsx";
import { SearchInput } from "../../v2/components/SearchInput.jsx";
import { ConsolidatedFilter } from "../../v2/components/ConsolidatedFilter.jsx";
import { SortControl } from "../../v2/components/SortControl.jsx";
import { CompactSwitch } from "../../v2/components/ViewControls.jsx";
import { SortableTh } from "./reportStyles.jsx";
import { KpiTile } from "../../components/KpiTile.jsx";
import { KPI_TILE_ROW_HEIGHT } from "../plan/KpiTileRow.jsx";
import { KpiBand } from "../../components/KpiBand.jsx";
import { formatReportingPeriodCoverageTooltip } from "../../services/reportingPeriod.js";

const PERIODS = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "weekly", label: "Weekly" },
];

const SORT_FIELDS = [
  { value: "office", label: "Office" },
  { value: "installedSeats", label: "Installed desks", numeric: true },
  { value: "usedAvgSeats", label: "Used (AVG)", numeric: true },
  { value: "avgAttendancePct", label: "Avg attendance (% of assigned)", numeric: true },
  { value: "peakCount", label: "Peak used count", numeric: true },
  { value: "peakPct", label: "Peak attendance (% of assigned)", numeric: true },
];

const COLUMNS = [
  ["Office", "office", null],
  ["Installed desks", "installedSeats", "Installed seat capacity"],
  ["Used (AVG)", "usedAvgSeats", null],
  ["Avg attendance (% of assigned)", "avgAttendancePct", "Average people present divided by assigned headcount"],
  ["Peak used count", "peakCount", "Office peak badge count; market rows sum office peaks (which may be on different days). Count, not a percentage"],
  ["Peak attendance (% of assigned)", "peakPct", "Office peak badges (or sum of office peaks for a market) divided by assigned headcount"],
  ["8-week seat utilization trend", null, null],
];

function officeName(row) {
  return [row.market, row.locationAlias || row.locationId || "Unnamed office"].filter(Boolean).join(" · ");
}

function sortRows(rows, sort) {
  return [...rows].sort((a, b) => {
    if (sort.key === "office") {
      const nameA = a.locationId ? officeName(a) : a.market ?? "";
      const nameB = b.locationId ? officeName(b) : b.market ?? "";
      const difference = nameA.localeCompare(nameB, undefined, { sensitivity: "base", numeric: true });
      if (difference) return sort.dir === "asc" ? difference : -difference;
    } else {
      const aValue = a[sort.key] == null ? null : Number(a[sort.key]);
      const bValue = b[sort.key] == null ? null : Number(b[sort.key]);
      const aMissing = aValue == null || !Number.isFinite(aValue);
      const bMissing = bValue == null || !Number.isFinite(bValue);
      if (aMissing !== bMissing) return aMissing ? 1 : -1;
      if (!aMissing && aValue !== bValue) return (aValue - bValue) * (sort.dir === "asc" ? 1 : -1);
    }
    return String(a.market ?? "").localeCompare(String(b.market ?? "")) ||
      String(a.locationAlias ?? a.locationId ?? "").localeCompare(String(b.locationAlias ?? b.locationId ?? ""));
  });
}

const cardStyle = {
  background: "var(--v2-card)",
  border: "1px solid var(--v2-line)",
  borderRadius: 12,
  ...TABLE_TOP_CORNERS,
};

function formatNumber(value, fallback = "—") {
  return value == null || !Number.isFinite(Number(value)) ? fallback : Math.round(Number(value)).toLocaleString();
}

function formatPct(value, fallback = "—") {
  return value == null || !Number.isFinite(Number(value)) ? fallback : `${Math.round(Number(value) * 100)}%`;
}

function formatMoney(value) {
  if (value == null || !Number.isFinite(Number(value))) return "—";
  const amount = Number(value);
  if (Math.abs(amount) >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`;
  if (Math.abs(amount) >= 1_000) return `$${Math.round(amount / 1_000)}K`;
  return `$${Math.round(amount).toLocaleString()}`;
}

function formatMonth(month) {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month ?? "")) return month ?? "—";
  const [year, number] = month.split("-").map(Number);
  return new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric", timeZone: "UTC" })
    .format(new Date(Date.UTC(year, number - 1, 1)));
}

function formatSeatsAsOf(month, period) {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month ?? "")) return "selected period end";
  const [year, number] = month.split("-").map(Number);
  if (period === "weekly") {
    return new Intl.DateTimeFormat("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" })
      .format(new Date(Date.UTC(year, number - 1, 7)));
  }
  if (period === "quarterly") {
    const quarterEndMonth = Math.ceil(number / 3) * 3;
    return `${formatMonth(`${year}-${String(quarterEndMonth).padStart(2, "0")}`)} (quarter end)`;
  }
  return `${formatMonth(month)} (month end)`;
}

function formatSeatsCaption(month, period) {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month ?? "")) return "selected period · offices in scope";
  const [year, number] = month.split("-").map(Number);
  const shortYear = String(year).slice(-2);
  if (period === "quarterly") return `Q${Math.ceil(number / 3)} '${shortYear} · offices in scope`;
  const shortMonth = new Intl.DateTimeFormat("en-US", { month: "short", timeZone: "UTC" })
    .format(new Date(Date.UTC(year, number - 1, 1)));
  return period === "weekly"
    ? `${shortMonth} 7 '${shortYear} · offices in scope`
    : `${shortMonth} '${shortYear} · offices in scope`;
}

function Sparkline({ values = [] }) {
  const finite = values.filter((value) => value != null && Number.isFinite(Number(value)));
  const max = finite.length ? Math.max(...finite, 0.01) : 1;
  return (
    <span
      data-testid="utilization-sparkline"
      style={{ display: "inline-flex", alignItems: "flex-end", gap: 2, height: 20, minWidth: 40 }}
      aria-label={`${values.length}-week seat utilization trend (presence divided by installed seats)`}
    >
      {Array.from({ length: 8 }, (_, index) => {
        const value = values[index];
        return (
          <i
            key={index}
            style={{
              display: "block",
              width: 4,
              height: value == null ? 3 : Math.max(3, Math.round((Number(value) / max) * 18)),
              borderRadius: 1,
              background: value == null ? "var(--v2-line)" : "var(--v2-green)",
            }}
          />
        );
      })}
    </span>
  );
}

function Controls({ viewMode, setViewMode, period, setPeriod, month, setMonth, months, marketsEnabled, source, updating }) {
  const [monthOpen, setMonthOpen] = useState(false);
  useEffect(() => {
    if (!monthOpen) return undefined;
    const closeOutside = (event) => {
      if (!event.target.closest('[data-dropdown-wrapper="utilization-month"]')) setMonthOpen(false);
    };
    document.addEventListener("mousedown", closeOutside);
    return () => document.removeEventListener("mousedown", closeOutside);
  }, [monthOpen]);
  const segmented = (options, value, onChange, testId) => (
    <div data-testid={testId} style={{ display: "inline-flex", border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden", background: T.card }}>
      {options.map((option) => {
        const item = typeof option === "string" ? { value: option, label: option } : option;
        const active = item.value === value;
        return (
          <button
            type="button"
            key={item.value}
            aria-pressed={active}
            onClick={() => onChange(item.value)}
            style={{
              border: 0,
              borderRight: `1px solid ${T.border}`,
              background: active ? T.dark : T.card,
              color: active ? T.card : T.mid,
              padding: "7px 12px",
              fontSize: T.fsSm,
              fontWeight: T.fwSemibold,
              cursor: "pointer",
            }}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
  return (
    <div data-testid="utilization-controls" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
      {marketsEnabled && segmented(["market", "office"].map((value) => ({ value, label: value[0].toUpperCase() + value.slice(1) })), viewMode, setViewMode, "utilization-view-toggle")}
      {segmented(PERIODS, period, setPeriod, "utilization-period-toggle")}
      {months.length > 0 && <div data-testid="utilization-month-picker" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: T.mid, fontSize: T.fsSm }}>
        <span>Month</span>
        <FilterDropdown id="utilization-month" label="" currentVal={formatMonth(month)} isFiltered={false} triggerMaxWidth={190}
          options={months.map((item) => ({ value: item, label: formatMonth(item) }))}
          selectedValue={month} isOpen={monthOpen} onToggle={() => setMonthOpen((open) => !open)}
          onSelect={(value) => { setMonth(value); setMonthOpen(false); }} />
      </div>}
      <span data-testid="utilization-source" style={{ marginLeft: "auto", fontSize: T.fsXs, fontWeight: T.fwBold, color: T.green, background: "var(--v2-green-tint)", border: `1px solid ${T.utilizationSourceBorder}`, borderRadius: 6, padding: "3px 9px" }}>
        Source: {source ?? "—"}
      </span>
      {updating && <span data-testid="utilization-updating" role="status" style={{ color: T.mid, fontSize: T.fsXs }}>Updating utilization…</span>}
    </div>
  );
}

function Kpis({ company, includeFinancial, period, month }) {
  const seatsDate = formatSeatsAsOf(month, period);
  const seatsPeriod = period === "weekly" ? "week" : period === "quarterly" ? "quarter" : "month";
  const seatsBasis = period === "weekly" ? "selected week" : period === "quarterly" ? "selected quarter" : "selected month";
  const seatsIntro = period === "monthly"
    ? "Seat capacity at the selected month end. Utilization rates use only capacity at offices with attendance rows. Home shows current capacity, which can be higher if seats were added since."
    : `Seat capacity at the ${seatsBasis} end. Utilization rates use only capacity at offices with attendance rows. Home shows current capacity, which can be higher if seats were added since.`;
  const definitions = [
    {
      id: "avg-attendance", label: "Avg attendance (% of assigned)", value: formatPct(company?.avgAttendancePct),
      descriptor: "Monday to Friday feed observations", color: T.dark,
      tooltip: `${formatNumber(company?.attendanceOfficeCount)} reporting of ${formatNumber(company?.officeCount)} offices in scope · people present ÷ assigned headcount · Monday to Friday dates excluding Ripple-wide and matching country holidays · missing feed days are not assumed to be zero · ${seatsPeriod}`,
    },
    {
      id: "peak-attendance", label: "Peak attendance (% of assigned)", value: formatPct(company?.peakPct),
      descriptor: "sum of office peaks", color: T.ragAmberText,
      tooltip: `sum of office peaks · assigned headcount · ${seatsPeriod}`,
    },
    {
      id: "installed-seats", label: "Installed seats", value: formatNumber(company?.installedSeats),
      descriptor: formatSeatsCaption(month, period).split(" · ")[0], color: T.dark,
      tooltip: `${formatSeatsCaption(month, period)}. ${seatsIntro} As of ${seatsDate}, this includes ${formatNumber(company?.officeCount)} operational and dormant offices in the selected ${seatsPeriod} and region. In-scope offices can include dormant offices without attendance observations; reporting offices are a separate subset.`,
    },
    {
      id: "used-avg-day", label: "Used (avg day)", value: formatNumber(company?.usedAvgSeats),
      descriptor: `${formatPct(company?.usedSeatPct)} utilization`, color: T.green,
      tooltip: `${formatPct(company?.usedSeatPct)} utilization · people present ÷ ${formatNumber(company?.avgInstalledSeats)} seats at offices with attendance rows · ${seatsPeriod}. Missing feed days are not counted as zero.`,
    },
    {
      id: "unused-at-peak", label: "Unused at peak", value: formatNumber(company?.unusedAtPeakSeats),
      descriptor: `${formatPct(company?.neverUsedPct)} never used`, color: T.red,
      tooltip: `${formatPct(company?.neverUsedPct)} of seats at offices with attendance rows, based on each office's highest observed day · ${seatsPeriod}. Missing feed days are not counted as zero.`,
    },
  ];
  if (includeFinancial) definitions.push({
    id: "rent-on-unused", label: "Rent on unused", value: formatMoney(company?.rentOnUnusedUsd),
    descriptor: "/yr, unused seats", color: T.red, tooltip: `/yr, seats no one uses · ${seatsPeriod}`,
  });
  return (
    <div data-testid="utilization-kpis" style={{ marginBottom: 16 }}>
      <KpiBand testId="utilization-kpi-row" ariaLabel="Report metrics">
        {definitions.map(({ id, label, value, descriptor, color, tooltip }) => (
          <KpiTile key={id} testId={`utilization-tile-${id}`} height={KPI_TILE_ROW_HEIGHT}
            label={label} value={value} descriptor={descriptor} scope="portfolio"
            color={color} tooltip={tooltip} />
        ))}
      </KpiBand>
    </div>
  );
}

function SeatBar({ company, includeFinancial, month }) {
  const used = company?.usedSeatPct == null ? 0 : Math.min(1, Math.max(0, company.usedSeatPct));
  const peak = company?.peakSeatPct == null ? null : Math.min(1, Math.max(used, company.peakSeatPct));
  const swing = peak == null ? 0 : Math.max(0, peak - used);
  return (
      <div data-testid="utilization-seat-bar" style={{ ...cardStyle, padding: "13px 15px", marginBottom: 14 }}>
      <div style={{ fontSize: T.fsXs, fontWeight: T.fwBold, letterSpacing: ".05em", textTransform: "uppercase", color: T.light, marginBottom: 9 }}>Company seat utilization (people present ÷ seats at offices with attendance rows) · {month ?? "selected period"}</div>
      <div style={{ display: "flex", height: 22, borderRadius: 6, overflow: "hidden", background: T.utilizationTrack }}>
        <div style={{ width: `${used * 100}%`, background: "var(--v2-green)" }} />
        <div style={{ width: `${swing * 100}%`, background: T.utilizationPeak }} />
      </div>
      <div style={{ display: "flex", gap: 16, marginTop: 9, fontSize: T.fsXs, color: T.mid, flexWrap: "wrap" }}>
        <span><b style={{ color: T.green }}>■</b> Avg utilization · {formatPct(used)} of seats at offices with attendance rows</span>
        <span><b style={{ color: T.utilizationPeakText }}>■</b> Combined office peaks · {formatPct(peak)} of those seats</span>
        <span><b style={{ color: T.utilizationUnused }}>■</b> Never used · {formatPct(company?.neverUsedPct)} of those seats</span>
        {includeFinancial && <span style={{ marginLeft: "auto", color: T.red, fontWeight: T.fwSemibold }}>≈ {formatMoney(company?.rentOnUnusedUsd)}/yr on seats no one uses</span>}
      </div>
    </div>
  );
}

function UtilizationTable({ viewMode, markets, offices, sort, onSort, compact, searchActive, reportingOfficeCount, scopeOfficeCount }) {
  const [expanded, setExpanded] = useState(() => new Set(markets?.[0]?.market ? [markets[0].market] : []));
  const headerRef = useRef(null);
  const bodyRef = useRef(null);
  useEffect(() => {
    setExpanded((current) => new Set([...current].filter((market) => markets?.some((row) => row.market === market))));
  }, [markets]);
  const rows = viewMode === "office" ? sortRows(offices, sort) : sortRows(markets, sort);
  const cellPadding = compact ? "6px 12px" : "11px 12px";
  const columnWidths = [270, 110, 110, 125, 100, 150, 95];
  const colgroup = <colgroup>{columnWidths.map((width, index) => <col key={index} style={{ width }} />)}</colgroup>;
  const tableStyle = { width: "100%", borderCollapse: "collapse", tableLayout: "fixed", minWidth: 960 };
  const renderRow = (row, isMarket) => (
    <React.Fragment key={isMarket ? row.market : row.locationId}>
      <tr>
        <td style={{ padding: cellPadding, borderBottom: `1px solid ${T.tintBorder}`, fontWeight: isMarket ? T.fwSemibold : T.fwMedium }}>
          {isMarket && <button type="button" aria-label={`Toggle ${row.market}`} onClick={() => setExpanded((current) => { const next = new Set(current); next.has(row.market) ? next.delete(row.market) : next.add(row.market); return next; })} style={{ border: 0, background: "transparent", color: T.light, cursor: "pointer", marginRight: 5 }}>{expanded.has(row.market) ? "▾" : "▸"}</button>}
          {!isMarket && <span style={{ display: "inline-block", paddingLeft: 26, color: T.blue }} />}
          {isMarket ? row.market : officeName(row)}
          {isMarket && <span style={{ color: T.light, fontSize: T.fsXs, fontWeight: T.fwRegular }}> · {row.officeCount} {row.officeCount === 1 ? "office" : "offices"}</span>}
          {isMarket && searchActive && <span style={{ color: T.light, fontSize: T.fsXs, fontWeight: T.fwRegular }}> · full market totals</span>}
        </td>
        <td style={{ textAlign: "right", padding: cellPadding, borderBottom: `1px solid ${T.tintBorder}` }}>{formatNumber(row.installedSeats)}</td>
        <td style={{ textAlign: "right", padding: cellPadding, borderBottom: `1px solid ${T.tintBorder}` }}>{formatNumber(row.usedAvgSeats)}</td>
        <td style={{ textAlign: "right", padding: cellPadding, borderBottom: `1px solid ${T.tintBorder}`, fontWeight: T.fwSemibold }}>{formatPct(row.avgAttendancePct)}</td>
        <td style={{ textAlign: "right", padding: cellPadding, borderBottom: `1px solid ${T.tintBorder}`, color: T.ragAmberText, fontWeight: T.fwSemibold }}>{formatNumber(row.peakCount)}</td>
        <td style={{ textAlign: "right", padding: cellPadding, borderBottom: `1px solid ${T.tintBorder}`, fontWeight: T.fwSemibold }}>{formatPct(row.peakPct)}</td>
        <td style={{ textAlign: "center", padding: cellPadding, borderBottom: `1px solid ${T.tintBorder}` }}><Sparkline values={row.trend} /></td>
      </tr>
      {isMarket && expanded.has(row.market) && sortRows(row.offices ?? [], sort).map((office) => renderRow(office, false))}
    </React.Fragment>
  );
  return (
    <div data-testid="utilization-table-card" style={cardStyle}>
      <div data-testid="utilization-sticky-header" style={{ position: "sticky", top: 0, zIndex: 4, background: T.lightGray, ...TABLE_TOP_CORNERS }}>
        <div ref={headerRef} data-testid="utilization-header-scroll" style={{ overflowX: "hidden", ...TABLE_TOP_CORNERS }}>
          <OverflowTable overflowScrollRef={bodyRef} style={tableStyle}>{colgroup}<thead><tr>
            {COLUMNS.map(([label, key, title], index) => key
              ? <SortableTh key={key} label={title ? <span title={title}>{label}</span> : label} sortKey={key}
                  currentSort={sort.key} onSort={onSort} align={index === 0 ? "left" : "right"}
                  ariaSort={sort.dir === "asc" ? "ascending" : "descending"} testIdPrefix="utilization" />
              : <th key={label} style={{ padding: "10px 12px", color: T.light, fontSize: T.fsXs, textTransform: "uppercase", textAlign: "center" }}>{label}</th>)}
          </tr></thead></OverflowTable>
        </div>
      </div>
      <div ref={bodyRef} data-testid="utilization-body-scroll" onScroll={() => { if (headerRef.current && bodyRef.current) headerRef.current.scrollLeft = bodyRef.current.scrollLeft; }} style={{ overflowX: "auto" }}>
        <table style={tableStyle}>{colgroup}<tbody>{rows.map((row) => renderRow(row, viewMode === "market"))}</tbody></table>
      </div>
      {!rows.length && <div data-testid="utilization-no-matches" style={{ padding: 24, textAlign: "center", color: T.mid }}>No offices match your search or filters.</div>}
      <div style={{ padding: "10px 12px", borderTop: `1px solid ${T.border}`, fontSize: T.fsXs, color: T.mid }}>
        {offices?.length ?? 0} office inventory rows · {formatNumber(reportingOfficeCount)} reporting of {formatNumber(scopeOfficeCount)} offices in scope (feed observations with assigned headcount on Monday to Friday dates); offices without observations have no attendance rate
      </div>
    </div>
  );
}

export function UtilizationReport({ onBack, prevReport, nextReport, onNavigateReport }) {
  const featureContext = useFeatureFlagContext();
  const eligibility = getReportNavEligibility(featureContext);
  const officesEnabled = eligibility.offices;
  const marketsEnabled = eligibility.markets;
  const sensitivity = useSensitivityPolicy();
  const includeFinancial = sensitivity.canSee("reports.utilization.rent_on_unused");
  const [viewMode, setViewMode] = useState("market");
  const [period, setPeriod] = useState("monthly");
  const [month, setMonth] = useState(null);
  const [region, setRegion] = useState("all");
  const [knownRegions, setKnownRegions] = useState([]);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: "office", dir: "asc" });
  const [compact, setCompact] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!eligibility.ready || !officesEnabled) {
      setData(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getUtilizationReport({ month: month || undefined, period, region: marketsEnabled ? region : undefined })
      .then((result) => {
        if (cancelled) return;
        setData(result);
        if (region === "all") setKnownRegions([...new Set((result.offices ?? []).map((row) => row.region).filter(Boolean))].sort());
        if (!month && result.month) setMonth(result.month);
      })
      .catch((err) => { if (!cancelled) setError(err); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [month, period, region, reloadKey, eligibility.ready, officesEnabled, marketsEnabled]);

  const months = data?.availableMonths ?? [];
  const canonicalMonth = data?.reportingPeriod?.month?.slice(0, 7);
  const canonicalMonthIncomplete = canonicalMonth === data?.month
    && data?.reportingPeriod?.isComplete === false;
  const coverageOfficeDays = data?.reportingPeriod?.eligibleOfficeDays;
  const observedOfficeDays = data?.reportingPeriod?.observedOfficeDays;
  const partialPeriodMessage = canonicalMonthIncomplete
    ? formatReportingPeriodCoverageTooltip(data.reportingPeriod)
    : null;
  const displayed = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    const matches = (office) => !query || officeName(office).toLocaleLowerCase().includes(query);
    const offices = (data?.offices ?? []).filter(matches);
    const markets = (data?.markets ?? []).flatMap((market) => {
      const children = (market.offices ?? []).filter((office) => !query || market.market?.toLocaleLowerCase().includes(query) || matches(office));
      return children.length ? [{ ...market, offices: children }] : [];
    });
    return { offices, markets };
  }, [data, search]);

  if (!officesEnabled || !eligibility.ready) {
    return <ReportAccessNotice reportName="Attendance & Utilization" loading={!eligibility.ready} onBack={onBack} />;
  }
  const effectiveViewMode = marketsEnabled ? viewMode : "office";

  return (
    <ReportShell
      recordNavigation
      onBack={onBack}
      prevReport={prevReport}
      nextReport={nextReport}
      onNavigateReport={onNavigateReport}
    >
      <div data-testid="utilization-report-body" style={{ maxWidth: 1400, margin: "0 auto", padding: "0 32px 48px", width: "100%" }}>
        {data?.offices?.length > 0 && <Kpis company={data.company} includeFinancial={includeFinancial} period={data.period ?? period} month={data.month} />}
        <Controls viewMode={effectiveViewMode} setViewMode={setViewMode} period={period} setPeriod={setPeriod} month={month} setMonth={setMonth} months={months} marketsEnabled={marketsEnabled} source={data?.attendanceSourceLabel} updating={loading && !!data} />
        <p data-testid="utilization-definition" style={{ margin: "-6px 0 14px", color: T.mid, fontSize: T.fsSm5, lineHeight: 1.5 }}>
          Attendance is people present ÷ assigned headcount; seat utilization is people present ÷ installed seats at offices with attendance rows.
          We include selected-feed observations Monday to Friday, excluding Ripple-wide closures and holidays listed for each office{"'"}s country.
          A day with no selected-feed row is not counted as zero.
        </p>
        {canonicalMonthIncomplete && (
          <div data-testid="utilization-partial-period" style={{ background: T.utilAmber, color: T.dark, border: `1px solid ${T.amber}`, borderRadius: 8, padding: "9px 12px", fontSize: T.fsSm, marginBottom: 14 }}>
            Partial reporting period: {partialPeriodMessage}
            {coverageOfficeDays > 0 && ` (${observedOfficeDays ?? 0} of ${coverageOfficeDays} eligible office-days observed).`}
          </div>
        )}
        <div data-testid="utilization-toolbar" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
          <SearchInput value={search} onChange={setSearch} placeholder="Search offices…" testId="utilization-search" />
          {marketsEnabled && <ConsolidatedFilter
            dimensions={[{ id: "region", label: "Region", options: knownRegions.map((value) => ({ value, label: value })) }]}
            selections={{ region: region === "all" ? [] : [region] }}
            onChange={({ region: selected = [] }) => setRegion(selected.length === knownRegions.length ? "all" : selected.at(-1) ?? "all")}
            testId="utilization-filter"
          />}
          <SortControl fields={SORT_FIELDS} primary={sort} secondary={{ key: null, dir: "asc" }}
            onChange={({ primary }) => setSort(primary)} defaultKey="office" testId="utilization-sort" />
          {(search.trim() || region !== "all" || sort.key !== "office" || sort.dir !== "asc") && (
            <button type="button" data-testid="utilization-clear-btn" onClick={() => { setSearch(""); setRegion("all"); setSort({ key: "office", dir: "asc" }); }}
              style={{ background: "transparent", border: "1px solid transparent", borderRadius: 8, color: "var(--v2-accent)", fontSize: T.fsBase, fontWeight: T.fwBold, cursor: "pointer", padding: "7px 11px" }}>Clear</button>
          )}
          <div style={{ marginLeft: "auto" }}><CompactSwitch checked={compact} onChange={() => setCompact((value) => !value)} testId="utilization-density-toggle" /></div>
        </div>
        {loading && !data && <div data-testid="utilization-loading" style={{ color: T.light, padding: 36, textAlign: "center" }}>Loading utilization…</div>}
        {error && !loading && <div data-testid="utilization-error" role="alert" style={{ color: T.red, padding: data ? "0 0 10px" : 28, textAlign: data ? "left" : "center" }}>Utilization could not be loaded. <button type="button" onClick={() => setReloadKey((value) => value + 1)}>Retry</button></div>}
        {data && (
          data.offices?.length ? (
            <>
              <SeatBar company={data.company} includeFinancial={includeFinancial} month={data.month} />
              <UtilizationTable viewMode={effectiveViewMode} markets={marketsEnabled ? displayed.markets : []} offices={displayed.offices}
                sort={sort} onSort={(key) => setSort((current) => ({ key, dir: current.key === key && current.dir === "asc" ? "desc" : "asc" }))}
                 compact={compact} searchActive={!!search.trim()} reportingOfficeCount={data.company?.attendanceOfficeCount} scopeOfficeCount={data.company?.officeCount} />
            </>
          ) : <div data-testid="utilization-empty" style={{ ...cardStyle, padding: 36, textAlign: "center", color: T.mid }}>No operational or dormant offices in scope for the selected period{marketsEnabled ? " or region" : ""}.</div>
        )}
      </div>
    </ReportShell>
  );
}